import { useSeoMeta } from '@unhead/react';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { ArrowLeftIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Layout } from '@/components/Layout';
import { ListItemEditor, type ListFormItem } from '@/components/ListItemEditor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useLinkList } from '@/hooks/useLinkLists';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { getKindConfig, SET_LIST_KINDS, resolveTagValue } from '@/lib/nip51';

export function EditListPage() {
  const { dTag } = useParams<{ dTag: string }>();
  const [searchParams] = useSearchParams();
  const kind = Number(searchParams.get('kind') ?? 30003);

  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending } = useNostrPublish();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: list, isLoading } = useLinkList(user?.pubkey, dTag, kind);
  const kindConfig = getKindConfig(kind);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [items, setItems] = useState<ListFormItem[]>([]);
  const [initialized, setInitialized] = useState(false);

  useSeoMeta({
    title: 'Edit List — Nostr Links',
    description: 'Edit your NIP-51 list.',
  });

  useEffect(() => {
    if (list && !initialized) {
      setTitle(list.title === kindConfig?.name ? '' : list.title);
      setDescription(list.description);
      setImage(list.image);
      setHashtags(list.hashtags.join(', '));
      setItems(
        list.items.map((item, i) => ({
          id: String(i),
          tag: item.tag,
          value: item.value,
          label: item.label,
        }))
      );
      setInitialized(true);
    }
  }, [list, initialized, kindConfig]);

  if (!user) {
    return (
      <Layout>
        <div className="container max-w-2xl py-16 text-center">
          <h1 className="text-2xl font-bold mb-2">Sign In Required</h1>
          <p className="text-muted-foreground">You need to sign in to edit a list.</p>
        </div>
      </Layout>
    );
  }

  if (!kindConfig) {
    return (
      <Layout>
        <div className="container max-w-2xl py-16 text-center">
          <h1 className="text-2xl font-bold mb-2">Unsupported Kind</h1>
          <p className="text-muted-foreground">This list kind is not supported for editing.</p>
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="container max-w-2xl py-8 space-y-4">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </Layout>
    );
  }

  if (!list) {
    return (
      <Layout>
        <div className="container max-w-2xl py-16 text-center">
          <h1 className="text-2xl font-bold mb-2">List Not Found</h1>
          <p className="text-muted-foreground">This list doesn&apos;t exist or you don&apos;t own it.</p>
        </div>
      </Layout>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validItems = items.filter((i) => i.value.trim().length > 0);

    const isSet = SET_LIST_KINDS.includes(kind);
    const tags: string[][] = [];

    if (isSet && list.dTag) {
      tags.push(['d', list.dTag]);
    }

    if (title.trim()) {
      tags.push(['title', title.trim()]);
    }

    if (description.trim()) {
      tags.push(['description', description.trim()]);
    }

    if (image.trim() && isSet) {
      tags.push(['image', image.trim()]);
    }

    // Nostree-specific labels for kind 30003
    if (kind === 30003) {
      tags.push(['L', 'me.nostree.ontology']);
      tags.push(['l', 'nostree']);
    }

    // Add list items
    for (const item of validItems) {
      const rawValue = resolveTagValue(item.value);
      if (item.tag === 'r') {
        const label = item.label.trim() || rawValue;
        tags.push(['r', rawValue, label]);
      } else if (item.tag === 'relay') {
        tags.push(['relay', rawValue]);
      } else if (item.tag === 'word') {
        tags.push(['word', rawValue.toLowerCase()]);
      } else {
        tags.push([item.tag, rawValue]);
      }
    }

    // Hashtags
    const parsedTags = hashtags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    for (const t of parsedTags) {
      tags.push(['t', t]);
    }

    // NIP-31 alt tag
    if (kind !== 1 && kind !== 30003) {
      tags.push(['alt', `NIP-51 ${kindConfig.name}: ${title.trim() || kindConfig.description}`]);
    }

    try {
      await publish({ kind, content: '', tags });

      queryClient.invalidateQueries({ queryKey: ['nostr', 'link-lists'] });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'link-list'] });

      toast({ title: 'Saved!', description: `Your ${kindConfig.name} has been updated.` });

      navigate(`/${nip19.npubEncode(user.pubkey)}`);
    } catch {
      toast({ title: 'Error', description: 'Failed to save list.', variant: 'destructive' });
    }
  };

  const showImageField = SET_LIST_KINDS.includes(kind);

  return (
    <Layout>
      <div className="container max-w-2xl py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeftIcon className="size-4" />
          Back
        </button>

        <h1 className="text-2xl font-bold mb-1">
          Edit {kindConfig.icon} {kindConfig.name}
        </h1>
        <p className="text-sm text-muted-foreground mb-6">{kindConfig.description}</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title{kindConfig.isSet ? '' : ' (optional)'}</Label>
            <Input
              id="title"
              placeholder={kindConfig.name}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder={`A brief description...`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          {showImageField && (
            <div className="space-y-2">
              <Label htmlFor="image">Cover Image URL (optional)</Label>
              <Input
                id="image"
                placeholder="https://example.com/image.jpg"
                value={image}
                onChange={(e) => setImage(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="hashtags">Tags (comma-separated, optional)</Label>
            <Input
              id="hashtags"
              placeholder="bitcoin, nostr, tech"
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
            />
          </div>

          <ListItemEditor
            kindConfig={kindConfig}
            items={items}
            onChange={setItems}
          />

          <Button type="submit" size="lg" className="w-full" disabled={isPending}>
            {isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
